import i18n from 'i18next';
import React, { useEffect, useState } from 'react';

import { Sockets } from 'core/sockets';

import GeneratingToast from '../components/generatingToast';

import styles from '../styles/useRealTimeTyping.module.scss';

const STATUS_TIMEOUT = 5000;
export const STATUS = {
  IDLE: 'idle',
  IN_PROGRESS: 'in-progress',
  SUCCESS: 'success',
  FAILURE: 'failure',
};

const useRealTimeTyping = ({
  channelName,
  handlers = {
    create: () => {},
    complete: () => {},
    connect: () => {},
    failure: () => {},
    status: () => {},
    success: () => {},
  },
  options = {},
}) => {
  const { defaultGenerateToastText = i18n.t('generating-content'), shouldAutoConnect = true } =
    options;

  const defaultHandler = () => {};

  const handlersProxy = new Proxy(handlers, {
    get: (target, prop) => (prop in target ? target[prop] : defaultHandler),
  });

  const [isConnected, setIsConnected] = useState(false);
  const [isSuccessful, setIsSuccessful] = useState(false);
  const [isFailure, setIsFailure] = useState(false);

  const [queue, setQueue] = useState([]);
  const [status, setStatus] = useState(STATUS.IDLE);
  const [typingContent, setTypingContent] = useState(null);

  const cleanup = () => {
    setStatus(STATUS.IDLE);
    setTypingContent(null);
    handlersProxy.complete();
  };

  const handleSuccess = () => {
    setTypingContent(null);
    handlersProxy.success();
    setStatus(STATUS.SUCCESS);
    setIsSuccessful(false);
    setTimeout(cleanup, STATUS_TIMEOUT);
  };

  const handleFailure = () => {
    setTypingContent(null);
    handlersProxy.failure();
    setStatus(STATUS.FAILURE);
    setIsFailure(false);
    setTimeout(cleanup, STATUS_TIMEOUT);
  };

  useEffect(() => {
    if (!queue.length) {
      if (isSuccessful) handleSuccess();
      else if (isFailure) handleFailure();
    }
  }, [queue, isSuccessful, isFailure]);

  const onTypingEnd = () => {
    setQueue((prevQueue) => {
      const currentQueue = prevQueue.slice(1);
      const message = currentQueue[0];

      if (message) {
        setTypingContent(message);
        handlersProxy.create({ message, onTypingEnd });
      }

      return currentQueue;
    });
  };

  const onAddMessage = (message) => {
    setQueue((prevQueue) => {
      if (!prevQueue.length) {
        setTypingContent(message);
        handlersProxy.create({ message, onTypingEnd });
        return [message];
      }
      return [...prevQueue, message];
    });
  };

  const onSuccess = () => {
    setIsSuccessful(true);
  };

  const onFailure = () => {
    setIsFailure(true);
  };

  const onCreateMessage = (payload) => {
    if (payload.event === 'create') onAddMessage(payload.message);
  };

  const onCompleteMessage = (payload) => {
    if (payload.event === 'complete') {
      if (payload.message.status === 'success') onSuccess();
      if (payload.message.status === 'failure' || payload.message.status === 'canceled')
        onFailure();
    }
  };

  const connect = () => {
    if (!channelName) {
      return console.error('channelName is not defined and must be defined to connect to sockets');
    }

    Sockets.on(channelName, { create: onCreateMessage, complete: onCompleteMessage });
    handlersProxy.connect();
    setStatus(STATUS.IN_PROGRESS);
    setIsConnected(true);
  };

  const disconnect = () => {
    Sockets.off(channelName, { create: onCreateMessage, update: onCompleteMessage });
    setIsConnected(false);
  };

  useEffect(() => {
    if (shouldAutoConnect) connect();

    return () => {
      disconnect();
    };
  }, []);

  return {
    connect,
    disconnect,
    glow: [styles.element, styles[status]],
    GeneratingToast: (props) => (
      <GeneratingToast
        status={status}
        defaultGenerateToastText={defaultGenerateToastText}
        {...props}
      />
    ),
    isConnected,
    queue,
    onCreateMessage,
    onCompleteMessage,
    onFailure,
    onTypingEnd,
    status,
    typingContent,
  };
};

export default useRealTimeTyping;
